/**
 * Custom hook for managing facet_meta API data
 * 
 * This hook provides functionality to:
 * - Fetch facet_meta data from the API
 * - Create mappings between IDs and SEO slugs
 * - Convert between old and new URL formats
 */

import { useAtom } from 'jotai';
import { 
  facetMetaDataAtom,
  facetMappingsAtom,
  facetMetaLoadingStatusAtom,
  facetMetaErrorAtom,
  fetchFacetMetaAtom,
  clearFacetMetaCacheAtom,
  getSeoSlugByIdAtom,
  getSeoSlugByLabelAtom,
  getIdBySeoSlugAtom,
  getLabelBySeoSlugAtom,
  mappingsReadyAtom
} from '../atom/facetMeta';
import { convertToSeoUrl, convertFromSeoUrl, buildSeoUrl, parseSeoUrl } from '../utils/seoUrlMapping'; 

// Custom hook for facet_meta functionality
export const useFacetMeta = () => {
  const [facetMetaData] = useAtom(facetMetaDataAtom);
  const [facetMappings] = useAtom(facetMappingsAtom);
  const [loading] = useAtom(facetMetaLoadingStatusAtom);
  const [error] = useAtom(facetMetaErrorAtom);
  const [, fetchFacetMeta] = useAtom(fetchFacetMetaAtom);
  const [, clearCache] = useAtom(clearFacetMetaCacheAtom);
  const [getSeoSlugById] = useAtom(getSeoSlugByIdAtom);
  const [getSeoSlugByLabel] = useAtom(getSeoSlugByLabelAtom);
  const [getIdBySeoSlug] = useAtom(getIdBySeoSlugAtom);
  const [getLabelBySeoSlug] = useAtom(getLabelBySeoSlugAtom);
  const [mappingsReady] = useAtom(mappingsReadyAtom);

  // Fetch facet_meta data
  const fetchData = async (query = '') => {
    try {
      await fetchFacetMeta(query);
    } catch (err) {
      console.error('Error fetching facet meta:', err);
      throw err;
    };
  }

  // Convert old URL parameters to new SEO URL
  const convertToSeoUrlParams = (params) => {
    if (!facetMappings) {
      console.warn('Facet mappings not available. Call fetchData first.');
      return null;
    }
    return convertToSeoUrl(params, facetMappings);
  };

  // Convert SEO URL parameters back to old format
  const convertFromSeoUrlParams = (seoParams) => {
    if (!facetMappings) {
      console.warn('Facet mappings not available. Call fetchData first.');
      return null;
    }
    return convertFromSeoUrl(seoParams, facetMappings);
  };

  // Build SEO URL from parameters
  const buildSeoUrlFromParams = (type, seoParams, queryParams = {}) => {
    return buildSeoUrl(type, seoParams, queryParams);
  };

  // Parse SEO URL to extract parameters
  const parseSeoUrlParams = (pathname, search = '') => {
    return parseSeoUrl(pathname, search);
  };

  // Get SEO slug by ID and facet type
  const getSeoSlug = (id, facetType) => {
    if (!getSeoSlugById) return null;
    return getSeoSlugById(id, facetType);
  };

  // Get SEO slug by label and facet type
  const getSeoSlugByLabelValue = (label, facetType) => {
    if (!getSeoSlugByLabel) return null;
    return getSeoSlugByLabel(label, facetType);
  };

  // Get ID by SEO slug and facet type
  const getId = (slug, facetType) => {
    if (!getIdBySeoSlug) return null;
    return getIdBySeoSlug(slug, facetType);
  };

  // Get label by SEO slug and facet type
  const getLabel = (slug, facetType) => {
    if (!getLabelBySeoSlug) return null;
    return getLabelBySeoSlug(slug, facetType);
  };

  // Get all available facet types
  const getAvailableFacetTypes = () => {
    if (!facetMappings) return [];
    return Object.keys(facetMappings).filter(key => !key.includes('_reverse'));
  };

  // Get all items for a specific facet type
  const getFacetItems = (facetType) => {
    if (!facetMetaData?.results?.facets?.[facetType]) return [];
    return facetMetaData.results.facets[facetType];
  };

  // Check if a specific facet type has items
  const hasFacetType = (facetType) => {
    return getFacetItems(facetType).length > 0;
  };

  // Get facet item by ID
  const getFacetItemById = (id, facetType) => {
    const items = getFacetItems(facetType);
    return items.find(item => item.id === parseInt(id)) || null;
  };

  // Get facet item by SEO slug
  const getFacetItemBySlug = (slug, facetType) => {
    const items = getFacetItems(facetType);
    return items.find(item => item.seo_slug === slug) || null;
  };

  return {
    // Data
    facetMetaData,
    facetMappings,
    loading,
    error,
    mappingsReady,
    
    // Actions
    fetchData,
    clearCache,
    
    // URL conversion utilities
    convertToSeoUrlParams,
    convertFromSeoUrlParams,
    buildSeoUrlFromParams,
    parseSeoUrlParams,
    
    // Mapping utilities
    getSeoSlug,
    getSeoSlugByLabelValue,
    getId,
    getLabel,
    
    // Facet utilities
    getAvailableFacetTypes,
    getFacetItems,
    hasFacetType,
    getFacetItemById,
    getFacetItemBySlug
  };
};

export default useFacetMeta;